import { ref } from 'vue';

// Module-level promise to ensure the script is loaded only once across all components.
let loadPromise: Promise<void> | null = null;

export function useRecaptcha(siteKey: string) {
  const error = ref<string | null>(null);

  const loadRecaptchaScript = (): Promise<void> => {
    if (loadPromise) {
      return loadPromise;
    }

    loadPromise = new Promise((resolve, reject) => {
      // Check if script is already on the page
      if (document.querySelector('script[src*="recaptcha/api.js"]')) {
        // If it exists, assume it will load or has loaded.
        // The ready check in getToken will handle waiting.
        return resolve();
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        resolve();
      };

      script.onerror = () => {
        const errorMessage = 'Failed to load Google reCAPTCHA script. Check network or ad-blockers.';
        error.value = errorMessage;
        console.error(errorMessage);
        reject(new Error(errorMessage));
      };

      document.head.appendChild(script);
    });

    return loadPromise;
  };

  const getToken = async (action: string): Promise<string> => {
    try {
      await loadRecaptchaScript();
    } catch (e) {
      console.error("Cannot get token because reCAPTCHA script failed to load.", e);
      throw e; // Re-throw to be caught by the caller
    }

    return new Promise((resolve, reject) => {
      if (!window.grecaptcha) {
        const errorMessage = 'window.grecaptcha is not available. The script may be blocked or failed to initialize.';
        console.error(errorMessage);
        return reject(new Error(errorMessage));
      }

      window.grecaptcha.ready(() => {
        try {
          window.grecaptcha.execute(siteKey, { action: action }).then((token) => {
            
            if (token) {
              resolve(token);
            } else {
              reject(new Error('reCAPTCHA execution returned no token.'));
            }
          }).catch(reject);
        } catch (e) {
          console.error('An exception occurred while executing reCAPTCHA:', e);
          reject(e);
        }
      });
    });
  };

  return { getToken };
}
